import { useCallback, useEffect, useMemo, useRef } from "react";
import useSearchContext from "@hooks/useSearchContext";

// ————————————————————————————————————————————————————————————————————————
// Debounce
const useDebouncedCallback = (fn: (...args: any[]) => void, delay = 250) => {
    const timeoutRef = useRef<number | undefined>();
    return useCallback(
        (...args: any[]) => {
            if (timeoutRef.current) window.clearTimeout(timeoutRef.current);
            timeoutRef.current = window.setTimeout(() => fn(...args), delay);
        },
        [fn, delay],
    );
};

// ————————————————————————————————————————————————————————————————————————
// Normalization helpers
const normalize = (s: string | undefined) =>
    (s || "")
        .toLowerCase()
        .normalize("NFD") // split accents
        .replace(/[\u0300-\u036f]/g, "") // remove accents
        .replace(/[\u2019'`]/g, "") // strip apostrophes
        .replace(/&/g, " and ") // common synonym
        .replace(/[^a-z0-9]+/g, " ") // keep letters/numbers as words
        .trim();

const squish = (s: string | undefined) => normalize(s).replace(/\s+/g, "");

const escapeRegExp = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const tokenize = (q: string) => normalize(q).split(/\s+/).filter(Boolean);

// Inclusion that checks (a) whole word, (b) normalized substring, (c) squished substring
const includesToken = (hay: string | undefined, token: string) => {
    if (!hay) return false;
    const hNorm = normalize(hay);
    const hSquish = squish(hay);
    const tNorm = normalize(token);
    const tSquish = squish(token);
    if (!tNorm && !tSquish) return false;

    // Prefer whole-word matches when possible
    if (tNorm) {
        const re = new RegExp(`\\b${escapeRegExp(tNorm)}\\b`);
        if (re.test(hNorm)) return true;
    }

    return (tNorm && hNorm.includes(tNorm)) || (tSquish && hSquish.includes(tSquish));
};

const arrayIncludesToken = (arr: string[] | undefined, token: string) =>
    !!arr && arr.some((s) => includesToken(s, token));

// ————————————————————————————————————————————————————————————————————————
export const useSearch = () => {
    const { products, query, setQuery, results, setResults, isSearching, setIsSearching } =
        useSearchContext();

    const tokens = useMemo(() => tokenize(query), [query]);
    const phrase = useMemo(() => normalize(query), [query]);
    const phraseSquished = useMemo(() => squish(query), [query]);

    const rank = useCallback(
        (p: any) => {
            // Stronger emphasis on title/tags; description is weak
            const WEIGHTS = {
                // existing weights…
                titleToken: 12,
                categoryToken: 6,
                tagsToken: 8,
                metaToken: 3,
                descToken: 1,
                titlePhrase: 18,
                categoryPhrase: 10,
                tagsPhrase: 10,
                metaPhrase: 5,
                descPhrase: 2,
                completenessPerToken: 2,

                taglineToken: 10,
                ingredientsToken: 7,
                usesToken: 6,
                benefitsToken: 4,

                taglinePhrase: 15,
                ingredientsPhrase: 12,
                usesPhrase: 10,
                benefitsPhrase: 9,
            } as const;

            let score = 0;
            const matchedTokens: string[] = [];
            let strongHits = 0; // title/category/tags/meta

            // Token hits
            tokens.forEach((t) => {
                let hit = false;

                if (includesToken(p.title, t)) {
                    score += WEIGHTS.titleToken;
                    hit = true;
                    strongHits++;
                }
                if (includesToken(p.category, t)) {
                    score += WEIGHTS.categoryToken;
                    hit = true;
                    strongHits++;
                }
                if (arrayIncludesToken(p.tags, t)) {
                    score += WEIGHTS.tagsToken;
                    hit = true;
                    strongHits++;
                }
                if (arrayIncludesToken(p.metaKeywords, t)) {
                    score += WEIGHTS.metaToken;
                    hit = true;
                    strongHits++;
                }
                if (includesToken(p.description, t)) {
                    score += WEIGHTS.descToken;
                    hit = true;
                }

                if (includesToken(p.tagline, t)) {
                    score += WEIGHTS.taglineToken;
                    hit = true;
                    strongHits++;
                }
                if (arrayIncludesToken(p.ingredients, t)) {
                    score += WEIGHTS.ingredientsToken;
                    hit = true;
                    strongHits++;
                }
                if (arrayIncludesToken(p.uses, t)) {
                    score += WEIGHTS.usesToken;
                    hit = true;
                    strongHits++;
                }
                if (arrayIncludesToken(p.benefits, t)) {
                    score += WEIGHTS.benefitsToken;
                    hit = true;
                    strongHits++;
                }

                if (hit) matchedTokens.push(t);
            });

            // Phrase boost — check both spaced and squished
            if (phrase) {
                const inTitle =
                    includesToken(p.title, phrase) || squish(p.title).includes(phraseSquished);
                const inCategory =
                    includesToken(p.category, phrase) ||
                    squish(p.category).includes(phraseSquished);
                const inTags =
                    arrayIncludesToken(p.tags, phrase) ||
                    (p.tags || []).some((t: string) => squish(t).includes(phraseSquished));
                const inMeta =
                    arrayIncludesToken(p.metaKeywords, phrase) ||
                    (p.metaKeywords || []).some((t: string) => squish(t).includes(phraseSquished));
                const inDesc =
                    includesToken(p.description, phrase) ||
                    squish(p.description).includes(phraseSquished);

                const inTagline =
                    includesToken(p.tagline, phrase) || squish(p.tagline).includes(phraseSquished);
                const inIngredients =
                    arrayIncludesToken(p.ingredients, phrase) ||
                    (p.ingredients || []).some((s: string) => squish(s).includes(phraseSquished));
                const inUses =
                    arrayIncludesToken(p.uses, phrase) ||
                    (p.uses || []).some((s: string) => squish(s).includes(phraseSquished));
                const inBenefits =
                    arrayIncludesToken(p.benefits, phrase) ||
                    (p.benefits || []).some((s: string) => squish(s).includes(phraseSquished));

                if (inTitle) score += WEIGHTS.titlePhrase;
                if (inCategory) score += WEIGHTS.categoryPhrase;
                if (inTags) score += WEIGHTS.tagsPhrase;
                if (inMeta) score += WEIGHTS.metaPhrase;
                if (inDesc) score += WEIGHTS.descPhrase;

                if (inTagline) score += WEIGHTS.taglinePhrase;
                if (inIngredients) score += WEIGHTS.ingredientsPhrase;
                if (inUses) score += WEIGHTS.usesPhrase;
                if (inBenefits) score += WEIGHTS.benefitsPhrase;
            }

            // Completeness — reward multiple distinct tokens
            const uniqueMatched = Array.from(new Set(matchedTokens));
            score += uniqueMatched.length * WEIGHTS.completenessPerToken;

            // Guard rails:
            // 1) Multi-word queries must match at least 2 tokens somewhere
            if (tokens.length >= 2 && uniqueMatched.length < 2) {
                score = 0; // discard weak generic matches like only “food”
            }

            // 2) If there were zero strong field hits, heavily down-weight description-only results
            if (score > 0 && strongHits === 0) {
                score *= 0.25;
            }

            return { score, matchedTokens: uniqueMatched };
        },
        [phrase, phraseSquished, tokens],
    );

    const runSearch = useCallback(
        (q: string) => {
            const normalized = q.trim();
            if (!normalized) {
                setResults([]);
                return;
            }
            const tkns = tokenize(normalized);
            if (!tkns.length) {
                setResults([]);
                return;
            }

            const ranked = products
                .map((p) => {
                    const { score, matchedTokens } = rank(p);
                    return { ...p, __score: score, __matches: matchedTokens };
                })
                .filter((r) => r.__score > 0)
                .sort((a: any, b: any) => {
                    // 1) isNew: true before false
                    const aNew = a?.isNew ? 1 : 0;
                    const bNew = b?.isNew ? 1 : 0;
                    if (bNew !== aNew) return bNew - aNew;

                    // 2) stock: higher first (treat missing as 0)
                    const aStock = Number.isFinite(a?.stock) ? a.stock : 0;
                    const bStock = Number.isFinite(b?.stock) ? b.stock : 0;
                    if (bStock !== aStock) return bStock - aStock;

                    // 3) isBestSeller: true before false
                    const aBest = a?.isBestSeller ? 1 : 0;
                    const bBest = b?.isBestSeller ? 1 : 0;
                    if (bBest !== aBest) return bBest - aBest;

                    // 4) fallbacks: relevance, tokens matched, title A–Z
                    if (b.__score !== a.__score) return b.__score - a.__score;
                    if (b.__matches.length !== a.__matches.length)
                        return b.__matches.length - a.__matches.length;
                    return a.title.localeCompare(b.title);
                });

            setResults(ranked);
        },
        [products, rank, setResults],
    );

    const debouncedSearch = useDebouncedCallback((q: string) => {
        setIsSearching(true);
        runSearch(q);
        setIsSearching(false);
    }, 150);

    useEffect(() => {
        debouncedSearch(query);
    }, [query, debouncedSearch]);

    return {
        query,
        setQuery,
        results,
        isSearching,
        search: runSearch,
    };
};
